import React from 'react';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import { PMCConfig, ProviderMatchProviderV9 } from '@kyruus/types';

import ExpandableCardContainer from '../ExpandableCardContainer/expandable-card-container';
import messages, { MessageType } from '../../lib/messages';

import { AboutHeader } from './styles';
import AboutContent from './about-content';
import { ClinicalSpecialInterests } from './clinical-special-interests';

import { TrackingFunction } from '../../lib/tracking-utils';
import {
  extractProviderDataContent,
  getAboutSectionSummaryData
} from '../../lib/utils';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';

interface ProviderAboutProps {
  config: PMCConfig;
  provider: ProviderMatchProviderV9;
  track: TrackingFunction;
  autolink?: {
    enabled?: boolean;
    customTLDs?: string[];
  };
}

const ProviderAbout = ({
  intl,
  config,
  provider,
  track,
  autolink
}: ProviderAboutProps & WrappedComponentProps): React.ReactElement => {
  /*
    Only provider fields that
      - are configured in config.provider_v9.summary_data
      - are not blacklisted in ABOUT_SECTION_EXCLUDE_SUMMARY_DATA_FIELDS
      - have data on the provider object
    are to be rendered here.
    */
  const aboutSectionSummaryData = getAboutSectionSummaryData(config, provider);
  const providerName = provider.name.full;
  const degrees = (provider.degrees || [])
    .map(({ name }: { name: string }) => name)
    .join(', ');

  return (
    <ExpandableCardContainer id="profile-about-section" track={track}>
      <AboutHeader>
        {intl.formatMessage(messages.profileAboutHeader, {
          providerName,
          degrees
        })}
      </AboutHeader>
      {aboutSectionSummaryData.map(
        (fieldConfig: {
          field: keyof ProviderMatchProviderV9;
          field_type?: string;
          subfield?: string;
        }) => {
          const { field, field_type, subfield } = fieldConfig;
          if (field === 'clinical_special_interests') {
            return <ClinicalSpecialInterests key={field} provider={provider} />;
          }
          const content = extractProviderDataContent(
            fieldConfig,
            provider as unknown as Record<string, unknown>
          );

          if (content == null) {
            return null;
          }

          // fields only display titles if there is a config.application_string_template defined for it
          // NOTE: titles are only intl'd if we remember to add messages for them
          const applicationStringTemplates =
            config.application_string_templates || {};
          const fieldPath = subfield ? `${field}.${subfield}` : field;
          const shouldDisplayTitle = Boolean(
            applicationStringTemplates[`field.name.${fieldPath}`]
          );
          const message =
            messages[`profileAbout_${String(fieldPath)}` as MessageType];
          const title = message
            ? intl.formatMessage(message)
            : applicationStringTemplates[`field.name.${fieldPath}`];
          const logoConfigs = (config.logos_v9 || []).filter(
            (logoConfig) => logoConfig.field === field
          );

          return (
            <AboutContent
              key={fieldPath}
              title={shouldDisplayTitle ? title : undefined}
              fieldType={field_type}
              content={content.toString()}
              logoConfigs={logoConfigs}
              provider={provider}
              dataTestId={`profile-about-${fieldPath}`}
              autolink={autolink}
            />
          );
        }
      )}
    </ExpandableCardContainer>
  );
};

export default withErrorBoundary(injectIntl(ProviderAbout));
